import React, { useEffect, useMemo, useState } from "react";
import Button from "@mui/material/Button";
import useDebouncedSearch from "hooks/useDebouncedSearch";
import { useTranslation } from "react-i18next";
import { PageContainer, SelectFilter } from "@outpave/ds";
import InvoicesTable from "../../components/admin/invoices/InvoicesTable";
import { useNavigate } from "react-router-dom";
import { InvoiceStatus } from "../../enums/InvoiceStatus";
import { invoiceStatusLabelKey } from "utils/invoices/invoiceStatus";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import Role from "enums/Role";

export type InvoiceTableQueryProps = {
  status?: string[];
  searchTerm?: string;
};

/**
 * Keyed rather than indexed: the table used to be told which tab was open and
 * derive its Paid Date column from `activeTab !== 0`.
 */
const TAB_CONFIGURATIONS = [
  {
    key: "active",
    labelKey: "Active/Pending",
    status: [
      InvoiceStatus.DRAFT,
      InvoiceStatus.PENDING,
      InvoiceStatus.ESTIMATE_APPROVED,
      InvoiceStatus.ESTIMATE_REJECTED,
      InvoiceStatus.ESTIMATE,
      InvoiceStatus.SENT,
      InvoiceStatus.PROCESSING_PAYMENT,
      InvoiceStatus.PAYMENT_FAILED,
    ],
  },
  {
    key: "paid",
    labelKey: "Paid",
    status: [InvoiceStatus.PAID, InvoiceStatus.COMPLETE],
  },
  {
    key: "all",
    labelKey: "Show All",
    status: Object.values(InvoiceStatus),
  },
] as const;

const InvoiceHistory: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTabKey, setActiveTabKey] = useState<string>(
    TAB_CONFIGURATIONS[0].key,
  );
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const search = useDebouncedSearch(setSearchTerm);

  const isAuditor = useCheckUserRole(Role.Auditor, { exact: true });

  const activeTab =
    TAB_CONFIGURATIONS.find((tab) => tab.key === activeTabKey) ??
    TAB_CONFIGURATIONS[0];

  // The DS ships no i18n, so the three strings SelectFilter renders itself
  // come from here.
  const filterLabels = useMemo(
    () => ({
      search: (label: string) =>
        t("SelectFilterSearch", { label: label.toLowerCase() }),
      noMatches: (label: string, query: string) =>
        t("SelectFilterNoMatches", { label: label.toLowerCase(), query }),
      available: (total: number) => t("SelectFilterAvailable", { total }),
    }),
    [t],
  );

  // Options come from the tab, so a status the tab excludes is never offerable.
  const statusOptions = useMemo(
    () =>
      activeTab.status.map((status) => ({
        value: status,
        label: t(invoiceStatusLabelKey(status)),
      })),
    [activeTab, t],
  );

  // The tab picks the working set and the filter narrows INSIDE it. Intersecting
  // rather than replacing is what stops a stale selection surviving a tab change
  // and emptying the table (OUT-1404's last-writer-wins bug).
  const filterCriteria: InvoiceTableQueryProps = useMemo(() => {
    const chosen = activeTab.status.filter((status) =>
      selectedStatuses.includes(status),
    );
    return {
      status: chosen.length > 0 ? chosen : [...activeTab.status],
      searchTerm,
    };
  }, [activeTab, selectedStatuses, searchTerm]);

  useEffect(() => {
    setSelectedStatuses([]);
  }, [activeTabKey]);

  const handleOpenSendInvoicePage = () => {
    navigate("./send-an-invoice");
  };

  return (
    <PageContainer
      title={t("Invoices")}
      primaryAction={
        !isAuditor && (
          <Button variant="primary-filled" onClick={handleOpenSendInvoicePage}>
            {t("Create an Invoice")}
          </Button>
        )
      }
      tabs={{
        items: TAB_CONFIGURATIONS.map((tab) => ({
          id: tab.key,
          label: t(tab.labelKey),
        })),
        activeId: activeTab.key,
        onChange: (id) => setActiveTabKey(id),
      }}
      search={search}
      filters={
        <SelectFilter
          multiple
          label={t("Status")}
          options={statusOptions}
          selected={selectedStatuses}
          onChange={(values) => setSelectedStatuses(values as string[])}
          labels={filterLabels}
        />
      }
    >
      <InvoicesTable
        filterCriteria={filterCriteria}
        showPaidDate={activeTab.key !== "active"}
      />
    </PageContainer>
  );
};

export default InvoiceHistory;
